"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader } from "lucide-react";
function DiscoverForm() {
  const {
    isSubmitting
  } = useWvcForm();
  const {
    control
  } = useWvcForm();
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
      {}
      <FormField control={control} name="preferred_location" rules={{
      required: "Location is required"
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-white/90">Preferred Location</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-white/5 border-white/20 text-white placeholder:text-white/50 focus:ring-accent/50 focus:border-accent">
                  <SelectValue placeholder="Select a location" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="London">London</SelectItem>
                <SelectItem value="Manchester">Manchester</SelectItem>
                <SelectItem value="Birmingham">Birmingham</SelectItem>
                <SelectItem value="Leeds">Leeds</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      <FormField control={control} name="property_type" render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-white/90">Property Type</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-white/5 border-white/20 text-white placeholder:text-white/50 focus:ring-accent/50 focus:border-accent">
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Studio">Studio</SelectItem>
                <SelectItem value="One Bedroom">One Bedroom</SelectItem>
                <SelectItem value="Two Bedroom">Two Bedroom</SelectItem>
                <SelectItem value="Three+ Bedroom">Three+ Bedroom</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      <FormField control={control} name="budget" rules={{
      required: "Budget is required",
      min: {
        value: 100,
        message: "Min £100"
      },
      max: {
        value: 10000,
        message: "Max £10,000"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-white/90">Max Budget (£)</FormLabel>
            <FormControl>
              <Input type="number" placeholder="1500" {...field} className="bg-white/5 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-accent/50 focus-visible:border-accent" />
            </FormControl>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      <FormField control={control} name="move_in_date" render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-white/90">Move-In Date</FormLabel>
            <FormControl>
              <Input type="date" {...field} className="bg-white/5 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-accent/50 focus-visible:border-accent [color-scheme:dark]" />
            </FormControl>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      {}
      <FormField control={control} name="name" rules={{
      required: "Full Name is required"
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-white/90">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" {...field} className="bg-white/5 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-accent/50 focus-visible:border-accent" />
            </FormControl>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^\S+@\S+$/i,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-white/90">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} className="bg-white/5 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-accent/50 focus-visible:border-accent" />
            </FormControl>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-white/90">Phone (Optional)</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+44 7123 456789" {...field} className="bg-white/5 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-accent/50 focus-visible:border-accent" />
            </FormControl>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold h-10 mt-auto">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Searching...
          </> : <>
            <Search className="mr-2 h-4 w-4" />
            Find Sanctuary
          </>}
      </Button>
    </div>;
}
export default function DiscoverYourSanctuary() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="discover-your-sanctuary" className="relative z-20 -mt-24 pb-16 px-4 sm:px-6 lg:px-8 pointer-events-none">
      <div ref={ref} className={`container mx-auto transition-all duration-700 transform pointer-events-auto ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="bg-[#1C1C1C]/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl p-6 lg:p-8">
          <div className="mb-6 text-center lg:text-left">
            <h2 className="text-2xl font-serif text-white mb-2">
              Discover Your Sanctuary
            </h2>
            <p className="text-white/70 text-sm">
              Tell us what you're looking for, and we'll find the perfect match.
            </p>
          </div>

          <FormProvider formId="discover_sanctuary_form_home" sectionName="discover-sanctuary" defaultValues={{
          name: "",
          email: "",
          phone: "",
          preferred_location: "",
          budget: "",
          property_type: "",
          move_in_date: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          preferred_location: {
            required: true
          },
          budget: {
            required: true,
            min: 100,
            max: 10000
          }
        }} submitText="Find Sanctuary" successMessage="We've received your preferences! An agent will contact you shortly with matches." errorMessage="Something went wrong. Please try again.">
            <Form className="w-full">
              <DiscoverForm />
            </Form>
          </FormProvider>
        </div>
      </div>
    </section>;
}